<template>
  <pu-map
    ref="puMap"
    v-bind="puMapProps"
    :seen-campsites="seenCampsitesStore.seenCampsites"
    :initial-zoom="initialZoom"
    :bounds-to-fit="boundsToFit"
    :show-layer-selector="true"
    class="pu-map"
    @boundsChange="$emit('boundsChange', $event)"
    @dragstart="$emit('dragstart')"
    @marker-clicked="onMarkerClicked"
    @marker-selected="onMarkerSelected"
  >
    <template #dynamicPopup>
      <div class="pu-infowindow">
        <slot name="dynamicPopup" />
      </div>
    </template>
  </pu-map>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { getWKTPoint } from '../../../../utility/geo/latLng'
import { logger } from '../../../../utility/logger'
import { SearchFilters } from '../../../maps-search/schemas'
import { PuMapProps } from '../pu-map/PuMapProps'
import { Marker, MarkerType } from '../pu-map/types'
import CampsitesMapBase from './CampsitesMapBase'
import { ResultCampsitesMapProps, ResultMapCampsite } from './CampsitesMapProps'

@Component({
  mixins: [CampsitesMapBase],
  emits: ['boundsChange', 'dragstart', 'marker-selected'],
  setup() {
    const seenCampsitesStore = useSeenCampsitesStore(usePinia())
    return { seenCampsitesStore }
  },
})
class CampsiteResultsMap extends Vue implements ResultCampsitesMapProps {
  @Prop({ required: true })
  campsites: ResultMapCampsite[]

  @Prop({ required: true })
  searchFilters: SearchFilters

  @Prop()
  boundsToFit?: any

  seenCampsitesStore: ReturnType<typeof useSeenCampsitesStore>

  get initialZoom() {
    return 6
  }

  get computedCentre() {
    logger('CampsiteResultsMap.centre', this.searchFilters.area)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Add proper types for search filters
    return this.searchFilters?.area?.lat && this.searchFilters?.area?.lng
      ? getWKTPoint(this.searchFilters.area.lat, this.searchFilters.area.lng)
      : this.centre
  }

  get puMapProps(): PuMapProps {
    return {
      markers: this.markers,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      initialBounds: this.initialBounds,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      circle: this.circle,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      visibleMarker: this.visibleMarker,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      centre: this.computedCentre,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      useClustering: this.useClustering,
      showGeolocationIcon: true,
      zoomControl: !!this.$isDesktop,
      scrollWheelZoom: true,
    }
  }

  // TODO: If we manage to check isFavourite in MapsMain then we can avoid this loop and pass it from the parent
  // considering looping over 1k+ objects this can be a good optimisation?
  get markers(): Marker[] {
    return this.campsites.map(({ slug, name, point, price, id }) => {
      return {
        campsiteId: id,
        name,
        position: point,
        price,
        slug,
        type: price ? MarkerType.price : MarkerType.icon,
      }
    })
  }

  onMarkerSelected(slug: string) {
    this.$emit('marker-selected', slug)
  }

  onMarkerClicked(marker) {
    this.$emit('marker-clicked', marker)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Add proper types for marker
    this.seenCampsitesStore.add(marker.slug)
  }

  getBounds() {
    const puMap = this.$refs.puMap
    if (puMap) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- TODO: Add proper types for PuMap ref
      return puMap.getBounds()
    }
  }
}

export default toNative(CampsiteResultsMap)
</script>
<style scoped>
.pu-map {
  width: 100%;
  height: 100%;
}
</style>
